import type { HintFocus, UserRole } from "@scoach/types";
import {
  CUSTOM_QUESTION_PRESETS,
  HINT_FOCUS_LABELS,
  resolveCustomQuestions,
} from "@scoach/types";
import { Notebook, Question, Spark } from "@scoach/ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";

import { api } from "../../../../lib/http.ts";
import { useAuthStore } from "../../../auth/store.ts";
import { groupHints } from "../hintGrouping.ts";
import { useLiveMeetingStore } from "../store.ts";
import { useAvailableRoles } from "../../../../lib/roles.ts";
import { CustomQuestionsLive } from "./CustomQuestionsLive.tsx";
import { FollowupList } from "./FollowupList.tsx";
import { HintGroupCard } from "./HintGroupCard.tsx";

export interface CoachColumnProps {
  meetingId: string;
  /** External meeting? The transcription-consent reminder only shows for external
   * meetings (internal meetings have no outside participants to notify). */
  isInternal?: boolean;
}

export function CoachColumn({ meetingId, isInternal }: CoachColumnProps) {
  const hints = useLiveMeetingStore((s) => s.hints);
  const pinnedIds = useLiveMeetingStore((s) => s.pinnedHintIds);
  const threshold = useLiveMeetingStore((s) => s.hintThreshold);
  const setThreshold = useLiveMeetingStore((s) => s.setHintThreshold);
  const hintFocus = useLiveMeetingStore((s) => s.hintFocus);
  const setHintFocusState = useLiveMeetingStore((s) => s.setHintFocus);
  function setHintFocus(focus: HintFocus) {
    setHintFocusState(focus);
    void api(`/meetings/${meetingId}/hint-focus`, { method: "PATCH", body: { focus } }).catch(
      () => {},
    );
  }
  // Live role + question-type — bias hints, follow-ups, infographic, and the
  // custom-questions tab. Persisted so a reconnect keeps the choice.
  const meetingRole = useLiveMeetingStore((s) => s.meetingRole);
  const setMeetingRoleState = useLiveMeetingStore((s) => s.setMeetingRole);
  function setMeetingRole(role: UserRole) {
    setMeetingRoleState(role);
    void api(`/meetings/${meetingId}/meeting-role`, { method: "PATCH", body: { role } }).catch(
      () => {},
    );
  }
  const questionKey = useLiveMeetingStore((s) => s.meetingCustomQuestionKey);
  const setQuestionKeyState = useLiveMeetingStore((s) => s.setMeetingCustomQuestionKey);
  function setQuestionKey(key: string) {
    setQuestionKeyState(key || null);
    void api(`/meetings/${meetingId}/custom-question-key`, {
      method: "PATCH",
      body: { key: key || null },
    }).catch(() => {});
  }
  const availableRoles = useAvailableRoles();
  const roleOptions = useMemo(() => {
    if (meetingRole && !availableRoles.includes(meetingRole)) {
      return [meetingRole, ...availableRoles];
    }
    return availableRoles;
  }, [availableRoles, meetingRole]);

  const activeTag = useLiveMeetingStore((s) => s.activeTagFilter);
  const setActiveTag = useLiveMeetingStore((s) => s.setActiveTagFilter);
  const togglePinned = useLiveMeetingStore((s) => s.togglePinned);
  const followupSets = useLiveMeetingStore((s) => s.followupSets);
  const followupCount = followupSets[followupSets.length - 1]?.items.length ?? 0;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Transcription-consent reminder: EXTERNAL meetings only (internal meetings have
  // no outside participants). Shown in the hint area until the rep confirms they've
  // notified participants. It is NOT auto-dismissed — it lives as the oldest card in
  // the hint list, so new hints stack above it (it flows to the end and never hides
  // new hints). Confirming records it on the meeting so the summary can reflect it.
  const [consentClosed, setConsentClosed] = useState(false);
  const showConsent = !isInternal && !consentClosed;
  function confirmNotified() {
    setConsentClosed(true);
    void api(`/meetings/${meetingId}`, {
      method: "PATCH",
      body: { participantsNotified: true },
    }).catch(() => {});
  }

  // Second tab under "follow-ups": the rep's own custom questions, answered live.
  const [fuTab, setFuTab] = useState<"followups" | "custom">("followups");
  const customAnswers = useLiveMeetingStore((s) => s.customSummary);
  // Count against the SAME question set the live answerer + "My questions" tab use
  // (the per-meeting role/type set, or the user's edited/default list) — not the raw
  // global default — so the X/Y total reflects the chosen set and updates when it changes.
  const customSettings = useAuthStore((s) => s.user?.settings);
  const customQuestionKey = useLiveMeetingStore((s) => s.meetingCustomQuestionKey);
  const customQuestions = resolveCustomQuestions(customQuestionKey ?? undefined, customSettings);
  const customAnsweredCount = useMemo(() => {
    const byQ = new Map(customAnswers.map((a) => [a.question.trim().toLowerCase(), a]));
    return customQuestions.filter((q) => {
      const a = byQ.get(q.trim().toLowerCase());
      return a && a.source !== "not-found" && a.answer.trim().length > 0;
    }).length;
  }, [customAnswers, customQuestions]);

  const filtered = useMemo(
    () =>
      hints.filter((h) => {
        if (h.confidence < threshold) return false;
        // Tag filter: when a keyword tag is active, keep only hints carrying it
        // (case-insensitive). Cleared by clicking the tag again or the banner.
        if (activeTag) {
          const tags = (h.tags ?? []).map((t) => t.toLowerCase());
          if (!tags.includes(activeTag.toLowerCase())) return false;
        }
        return true;
      }),
    [hints, threshold, activeTag],
  );

  // Group same-topic hints (and drop near-duplicates) so repeats don't pile up.
  const groups = useMemo(() => groupHints(filtered), [filtered]);
  const pinnedGroups = useMemo(
    () => groups.filter((g) => g.some((h) => pinnedIds.has(h.id))),
    [groups, pinnedIds],
  );
  const unpinnedGroups = useMemo(
    () => groups.filter((g) => !g.some((h) => pinnedIds.has(h.id))),
    [groups, pinnedIds],
  );
  const visibleCount = useMemo(() => groups.reduce((a, g) => a + g.length, 0), [groups]);

  // New hints are prepended (newest at top). Only auto-scroll to the top when the user
  // is ALREADY at the top; if they've scrolled down to read earlier hints, don't yank
  // them back — flag "new hint" instead and let them click to jump up.
  const [hasNewHint, setHasNewHint] = useState(false);
  const prevGroupLenRef = useRef(unpinnedGroups.length);
  useEffect(() => {
    const el = scrollRef.current;
    const grew = unpinnedGroups.length > prevGroupLenRef.current;
    prevGroupLenRef.current = unpinnedGroups.length;
    if (!el || unpinnedGroups.length === 0) return;
    if (el.scrollTop <= 24) {
      el.scrollTop = 0;
      setHasNewHint(false);
    } else if (grew) {
      setHasNewHint(true);
    }
  }, [unpinnedGroups.length]);

  function onCoachScroll() {
    const el = scrollRef.current;
    if (el && el.scrollTop <= 24) setHasNewHint(false);
  }

  function scrollToNewHint() {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    setHasNewHint(false);
  }

  const setRightTab = useLiveMeetingStore((s) => s.setRightPanelTab);
  const setGenerating = useLiveMeetingStore((s) => s.setInfographicGenerating);

  // Pin only — sticks the hint to the top (the coach-pinned block). No infographic.
  function handlePin(hintId: string) {
    togglePinned(hintId);
  }

  // Image icon — generate an infographic for this hint's topic.
  function handleHintInfographic(hintId: string) {
    const hint = hints.find((h) => h.id === hintId);
    if (!hint) return;
    setGenerating(true);
    setRightTab("infographic");
    void api(`/meetings/${meetingId}/generate-infographic`, {
      method: "POST",
      body: { hintTopic: hint.title },
    }).catch(() => {
      setGenerating(false);
    });
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__sallyPinHint = handlePin;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__sallyHintInfographic = handleHintInfographic;
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).__sallyPinHint;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).__sallyHintInfographic;
    };
  });

  return (
    <section className="panel coach-panel coach-hero">
      <section className="panel" style={{ minHeight: 0 }}>
        <div className="panel-head">
          <div
            className="panel-title-row"
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}
          >
            <div className="seg seg-tabs" style={{ pointerEvents: "none", flex: 1 }}>
              <button type="button" className="on">
                <Spark size={14} /> Live coaching <span className="tab-count">{visibleCount}</span>
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 10,
                color: "var(--text-4)",
              }}
            >
              <select
                className="coach-focus-select"
                value={meetingRole ?? ""}
                onChange={(e) => setMeetingRole(e.target.value as UserRole)}
                title="Your role for this meeting — biases hints, follow-ups & the infographic"
              >
                <option value="" disabled>
                  Role…
                </option>
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <select
                className="coach-focus-select"
                value={questionKey ?? ""}
                onChange={(e) => setQuestionKey(e.target.value)}
                title="Question set — the topics you want covered (also shapes follow-ups)"
              >
                <option value="">Questions…</option>
                {Object.keys(CUSTOM_QUESTION_PRESETS).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <select
                className="coach-focus-select"
                value={hintFocus}
                onChange={(e) => setHintFocus(e.target.value as HintFocus)}
                title="Focus type — shapes the hints (and, for Project manager, the summary)"
              >
                {(Object.keys(HINT_FOCUS_LABELS) as HintFocus[]).map((f) => (
                  <option key={f} value={f}>
                    {HINT_FOCUS_LABELS[f]}
                  </option>
                ))}
              </select>
              <span>Min</span>
              <input
                type="range"
                min={0.5}
                max={1}
                step={0.01}
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                style={{ width: 60, accentColor: "var(--gc-blue)" }}
                title={`Confidence threshold: ${Math.round(threshold * 100)}%`}
              />
              <span className="mono">{Math.round(threshold * 100)}%</span>
            </div>
          </div>
        </div>

        {activeTag && (
          <div className="coach-tag-filter">
            <span>
              Filtered by tag: <strong>{activeTag}</strong>
            </span>
            <button type="button" className="ghost-btn" onClick={() => setActiveTag(null)}>
              Clear
            </button>
          </div>
        )}

        {pinnedGroups.length > 0 && (
          <div className="coach-pinned">
            {pinnedGroups.map((g) => (
              <HintGroupCard key={g[0]!.id} group={g} meetingId={meetingId} />
            ))}
          </div>
        )}

        <div
          style={{ position: "relative", minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          {hasNewHint && (
            <button
              type="button"
              onClick={scrollToNewHint}
              title="Jump to the newest hint"
              style={{
                position: "absolute",
                top: 6,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 5,
                background: "var(--gc-blue)",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "4px 12px",
                fontSize: 12,
                fontWeight: 600,
                boxShadow: "0 2px 8px rgba(0,0,0,.2)",
                cursor: "pointer",
              }}
            >
              ↑ New hint
            </button>
          )}
          <div className="coach-scroll scroll" ref={scrollRef} onScroll={onCoachScroll}>
            {unpinnedGroups.map((g) => (
              <HintGroupCard key={g[0]!.id} group={g} meetingId={meetingId} />
            ))}

            {showConsent && (
              <div
                role="alert"
                style={{
                  margin: "0 0 10px",
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "rgba(217,48,37,.10)",
                  borderLeft: "4px solid var(--gc-red, #d93025)",
                  border: "1px solid rgba(217,48,37,.5)",
                  borderLeftWidth: 4,
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 13.5,
                    lineHeight: 1.45,
                    color: "var(--gc-red, #d93025)",
                  }}
                >
                  ⚠️ Before you start: Let all participants know this meeting will be transcribed.
                  Only the text is saved — no audio is stored.
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontWeight: 600,
                    fontSize: 12.5,
                    lineHeight: 1.45,
                    color: "var(--text-2)",
                  }}
                >
                  Note: You can delete the transcript after the call at any time, or update your
                  settings to disable transcript saving completely.
                </div>
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="pill-btn primary" onClick={confirmNotified}>
                    ✓ I've notified everyone
                  </button>
                </div>
              </div>
            )}

            {filtered.length === 0 && (
              <div className="hint-thinking hint-in">
                <div className="ht-row">
                  <span className="dot dot-pulse" style={{ background: "var(--gc-blue)" }} />
                  <span
                    className="dot dot-pulse"
                    style={{ background: "var(--gc-blue)", animationDelay: "160ms" }}
                  />
                  <span
                    className="dot dot-pulse"
                    style={{ background: "var(--gc-blue)", animationDelay: "320ms" }}
                  />
                  <span className="ht-label">Listening for the conversation…</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="panel coach-hero-followups">
        <div className="panel-head">
          <div className="seg seg-tabs">
            <button
              type="button"
              className={fuTab === "followups" ? "on" : ""}
              onClick={() => setFuTab("followups")}
            >
              <Question size={14} /> Suggested follow-ups{" "}
              <span className="tab-count">{followupCount}</span>
            </button>
            <button
              type="button"
              className={fuTab === "custom" ? "on" : ""}
              onClick={() => setFuTab("custom")}
              title="Your custom questions, answered live from the conversation"
            >
              <Notebook size={14} /> My questions{" "}
              <span className="tab-count">
                {customAnsweredCount}/{customQuestions.length}
              </span>
            </button>
          </div>
        </div>
        <div className="fu-scroll">
          {fuTab === "followups" ? <FollowupList /> : <CustomQuestionsLive />}
        </div>
      </section>
    </section>
  );
}
